import type {
  EnterpriseNotification,
} from "@swot/swot-components";

import {
  readDateIso,
  readString,
} from "./mapperUtils.js";

export type CmtsNotification =
  EnterpriseNotification &
  Record<string, unknown> & {
    readonly readState:
      | "read"
      | "unread";

    readonly notificationState:
      string;

    readonly notificationType?:
      | string
      | undefined;

    readonly eventType?:
      | string
      | undefined;

    readonly linkedEntityType?:
      | string
      | undefined;

    readonly linkedEntityId?:
      | string
      | undefined;

    readonly complaintId?:
      | string
      | undefined;

    readonly parentComplaintId?:
      | string
      | undefined;

    readonly linkedRecordCode?:
      | string
      | undefined;

    readonly linkedRecordTitle?:
      | string
      | undefined;

    readonly complaintNumber?:
      | string
      | undefined;

    readonly complaintCode?:
      | string
      | undefined;

    readonly complaintTitle?:
      | string
      | undefined;

    readonly actionCode?:
      | string
      | undefined;

    readonly body?:
      | string
      | undefined;

    readonly severity?:
      | string
      | undefined;

    readonly allowedActions:
      readonly string[];
  };

function readBoolean(
  dto: unknown,
  keys: readonly string[],
  fallback: boolean,
): boolean {
  if (
    typeof dto !== "object" ||
    dto === null
  ) {
    return fallback;
  }

  const record =
    dto as Record<
      string,
      unknown
    >;

  for (const key of keys) {
    if (
      typeof record[key] ===
      "boolean"
    ) {
      return record[key] as boolean;
    }
  }

  return fallback;
}

function readStringArray(
  dto: unknown,
  keys: readonly string[],
): string[] {
  if (
    typeof dto !== "object" ||
    dto === null
  ) {
    return [];
  }

  const record =
    dto as Record<
      string,
      unknown
    >;

  for (const key of keys) {
    const value =
      record[key];

    if (
      Array.isArray(value)
    ) {
      return value
        .map((item) =>
          String(item).trim(),
        )
        .filter(Boolean);
    }
  }

  return [];
}

export function mapNotification(
  dto: unknown,
): CmtsNotification {
  const notificationState =
    readString(
      dto,
      [
        "notificationState",
        "NotificationState",

        "status",
        "Status",

        "readStatus",
        "ReadStatus",

        "dispatchStatus",
        "DispatchStatus",
      ],
      "UNREAD",
    )
      .trim()
      .toUpperCase();

  const isRead =
    readBoolean(
      dto,
      [
        "isRead",
        "IsRead",
        "read",
        "Read",
      ],
      [
        "READ",
        "ACKNOWLEDGED",
        "ARCHIVED",
      ].includes(
        notificationState,
      ),
    );

  const linkedEntityId =
    readString(
      dto,
      [
        "linkedEntityId",
        "LinkedEntityId",

        "entityId",
        "EntityId",

        "complaintId",
        "ComplaintId",

        "parentComplaintId",
        "ParentComplaintId",

        "linkedComplaintId",
        "LinkedComplaintId",

        "sourceEntityId",
        "SourceEntityId",
      ],
      "",
    );

  const complaintId =
    readString(
      dto,
      [
        "complaintId",
        "ComplaintId",

        "parentComplaintId",
        "ParentComplaintId",

        "linkedComplaintId",
        "LinkedComplaintId",

        "linkedEntityId",
        "LinkedEntityId",

        "sourceEntityId",
        "SourceEntityId",
      ],
      "",
    );

  const parentComplaintId =
    readString(
      dto,
      [
        "parentComplaintId",
        "ParentComplaintId",

        "complaintId",
        "ComplaintId",

        "linkedComplaintId",
        "LinkedComplaintId",

        "linkedEntityId",
        "LinkedEntityId",
      ],
      "",
    );

  const linkedRecordCode =
    readString(
      dto,
      [
        "linkedRecordCode",
        "LinkedRecordCode",

        "linkedEntityCode",
        "LinkedEntityCode",

        "linkedEntityNumber",
        "LinkedEntityNumber",

        "sourceDisplayCode",
        "SourceDisplayCode",

        "displayCode",
        "DisplayCode",

        "complaintNumber",
        "ComplaintNumber",

        "complaintCode",
        "ComplaintCode",

        "registrationNumber",
        "RegistrationNumber",

        "referenceNumber",
        "ReferenceNumber",
      ],
      "",
    );

  const linkedRecordTitle =
    readString(
      dto,
      [
        "linkedRecordTitle",
        "LinkedRecordTitle",

        "linkedEntityTitle",
        "LinkedEntityTitle",

        "complaintTitle",
        "ComplaintTitle",

        "sourceTitle",
        "SourceTitle",

        "entityTitle",
        "EntityTitle",
      ],
      "",
    );

  const complaintNumber =
    readString(
      dto,
      [
        "complaintNumber",
        "ComplaintNumber",

        "complaintCode",
        "ComplaintCode",

        "registrationNumber",
        "RegistrationNumber",

        "linkedRecordCode",
        "LinkedRecordCode",
      ],
      linkedRecordCode,
    );

  const complaintCode =
    readString(
      dto,
      [
        "complaintCode",
        "ComplaintCode",

        "complaintNumber",
        "ComplaintNumber",

        "registrationNumber",
        "RegistrationNumber",

        "linkedRecordCode",
        "LinkedRecordCode",
      ],
      linkedRecordCode,
    );

  const complaintTitle =
    readString(
      dto,
      [
        "complaintTitle",
        "ComplaintTitle",

        "linkedRecordTitle",
        "LinkedRecordTitle",

        "linkedEntityTitle",
        "LinkedEntityTitle",

        "sourceTitle",
        "SourceTitle",
      ],
      linkedRecordTitle,
    );

  return {
    id:
      readString(
        dto,
        [
          "id",
          "Id",

          "notificationId",
          "NotificationId",
        ],
        "",
      ),

    title:
      readString(
        dto,
        [
          "title",
          "Title",

          "subject",
          "Subject",

          "message",
          "Message",

          "body",
          "Body",
        ],
        "CMTS notification",
      ),

    channel:
      normalizeChannel(
        readString(
          dto,
          [
            "channel",
            "Channel",

            "channelCode",
            "ChannelCode",
          ],
          "in_app",
        ),
      ),

    recipientId:
      readString(
        dto,
        [
          "recipientUserId",
          "RecipientUserId",

          "recipientId",
          "RecipientId",

          "userId",
          "UserId",
        ],
        "",
      ),

    status:
      normalizeDispatchStatus(
        readString(
          dto,
          [
            "dispatchStatus",
            "DispatchStatus",

            "deliveryStatus",
            "DeliveryStatus",

            "status",
            "Status",
          ],
          "queued",
        ),
      ),

    createdAt:
      readDateIso(
        dto,
        [
          "createdAt",
          "CreatedAt",

          "createdAtUtc",
          "CreatedAtUtc",

          "queuedAtUtc",
          "QueuedAtUtc",

          "occurredAt",
          "OccurredAt",

          "sentAt",
          "SentAt",
        ],
        "",
      ),

    readState:
      isRead
        ? "read"
        : "unread",

    notificationState,

    notificationType:
      readString(
        dto,
        [
          "notificationType",
          "NotificationType",

          "eventType",
          "EventType",

          "type",
          "Type",
        ],
        "",
      ) || undefined,

    eventType:
      readString(
        dto,
        [
          "eventType",
          "EventType",

          "notificationType",
          "NotificationType",

          "type",
          "Type",
        ],
        "",
      ) || undefined,

    linkedEntityType:
      readString(
        dto,
        [
          "linkedEntityType",
          "LinkedEntityType",

          "entityType",
          "EntityType",

          "sourceModule",
          "SourceModule",
        ],
        complaintId
          ? "COMPLAINT"
          : "",
      ) || undefined,

    linkedEntityId:
      linkedEntityId ||
      undefined,

    complaintId:
      complaintId ||
      undefined,

    parentComplaintId:
      parentComplaintId ||
      undefined,

    linkedRecordCode:
      linkedRecordCode ||
      undefined,

    linkedRecordTitle:
      linkedRecordTitle ||
      undefined,

    complaintNumber:
      complaintNumber ||
      undefined,

    complaintCode:
      complaintCode ||
      undefined,

    complaintTitle:
      complaintTitle ||
      undefined,

    actionCode:
      readString(
        dto,
        [
          "actionCode",
          "ActionCode",

          "primaryActionCode",
          "PrimaryActionCode",
        ],
        "",
      ) || undefined,

    body:
      readString(
        dto,
        [
          "body",
          "Body",

          "message",
          "Message",

          "summary",
          "Summary",

          "description",
          "Description",
        ],
        "",
      ) || undefined,

    severity:
      readString(
        dto,
        [
          "severity",
          "Severity",

          "severityCode",
          "SeverityCode",

          "priority",
          "Priority",

          "priorityCode",
          "PriorityCode",
        ],
        "",
      ) || undefined,

    allowedActions:
      readStringArray(
        dto,
        [
          "allowedActions",
          "AllowedActions",

          "capabilities",
          "Capabilities",
        ],
      ),
  };
}

function normalizeChannel(
  value: string,
): EnterpriseNotification["channel"] {
  const normalizedValue =
    value
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, "_");

  if (
    normalizedValue === "email" ||
    normalizedValue === "sms" ||
    normalizedValue === "whatsapp" ||
    normalizedValue === "webhook"
  ) {
    return normalizedValue;
  }

  return "in_app";
}

function normalizeDispatchStatus(
  value: string,
): EnterpriseNotification["status"] {
  const normalizedValue =
    value
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, "_");

  if (
    normalizedValue === "pending" ||
    normalizedValue === "created" ||
    normalizedValue === "new" ||
    normalizedValue === "unread"
  ) {
    return "queued";
  }

  if (
    normalizedValue === "read" ||
    normalizedValue === "acknowledged"
  ) {
    return "sent";
  }

  if (
    normalizedValue === "sent" ||
    normalizedValue === "failed" ||
    normalizedValue === "suppressed" ||
    normalizedValue === "queued"
  ) {
    return normalizedValue;
  }

  return "queued";
}